import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { COLOR_PRIMARY } from 'src/utils/const';
import {
  TutorialType,
  TutorialNavLesson,
  tutorialContent,
  tutorialNavModules,
  TUTORIAL_COURSE_HEADING,
} from './tutorialConfig';

interface TutorialModalProps {
  open: boolean;
  onClose: () => void;
  type: TutorialType;
  noProgressBar?: boolean;
  headerTitle?: string;
  headerDescription?: string;
  continueButtonLabel?: string;
  suppressOtherTutorials?: boolean;
}

const TutorialModal: React.FC<TutorialModalProps> = ({
  open,
  onClose,
  type,
  noProgressBar,
  headerTitle,
  headerDescription,
  continueButtonLabel,
  suppressOtherTutorials,
}) => {
  const [progress, setProgress] = useState(0);
  const [currentTutorialType, setCurrentTutorialType] = useState<TutorialType>(type);
  const content = tutorialContent[currentTutorialType];
  const isInitialTutorialType = currentTutorialType === type;
  const displayTitle =
    isInitialTutorialType && headerTitle ? headerTitle : content.title;
  const showHeaderDescription = Boolean(isInitialTutorialType && headerDescription);

  const showNavPanel = !(suppressOtherTutorials && isInitialTutorialType);

  useEffect(() => {
    if (open) {
      setCurrentTutorialType(type);
      setProgress(0);
    }
  }, [open, type]);

  useEffect(() => {
    if (noProgressBar) {
      return;
    }

    if (open) {
      setProgress(0);
      const timer = setInterval(() => {
        setProgress(prevProgress => {
          if (prevProgress >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prevProgress + 1;
        });
      }, 150);

      return () => {
        clearInterval(timer);
      };
    }
    return () => {};
  }, [open, currentTutorialType]);

  useEffect(() => {
    return () => {
      localStorage.removeItem('show_neighborhood_tutorial');
    };
  }, []);

  const handleContinue = () => {
    localStorage.removeItem('show_neighborhood_tutorial');
    onClose();
  };

  const handleTutorialClick = (tutorialType: TutorialType) => {
    setCurrentTutorialType(tutorialType);
    setProgress(0);
  };

  const paddingLeftForDepth = (depth: number) => 12 + depth * 12;

  const renderClickableRow = (item: TutorialNavLesson, depth: number) => {
    const active = item.tutorialKey === currentTutorialType;
    const comingSoon = Boolean(item.comingSoon);
    const clickable = !comingSoon && !!item.tutorialKey;

    return (
      <Box
        key={item.id}
        onClick={() => {
          if (clickable && item.tutorialKey) {
            handleTutorialClick(item.tutorialKey);
          }
        }}
        sx={{
          padding: '6px 12px',
          paddingLeft: `${paddingLeftForDepth(depth)}px`,
          borderRadius: '8px',
          cursor: clickable ? 'pointer' : 'default',
          backgroundColor: active ? COLOR_PRIMARY + '14' : 'transparent',
          transition: 'background-color 0.15s',
          ...(clickable &&
            !active && {
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '12.5px',
            fontWeight: active ? 600 : 400,
            color: comingSoon ? 'text.disabled' : active ? COLOR_PRIMARY : 'text.primary',
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {item.label}
          {comingSoon && (
            <Chip
              label="coming soon"
              size="small"
              sx={{
                height: '18px',
                fontSize: '10px',
                color: 'text.disabled',
                backgroundColor: '#f0f0f0',
                '& .MuiChip-label': { px: '6px' },
              }}
            />
          )}
        </Typography>
      </Box>
    );
  };

  const renderLesson = (item: TutorialNavLesson, depth: number): React.ReactNode => {
    const hasChildren = Boolean(item.children?.length);
    const hasVideoOrPlaceholder = Boolean(item.tutorialKey || item.comingSoon);

    if (hasChildren && !hasVideoOrPlaceholder) {
      return (
        <Box key={item.id} sx={{ mb: 0.25 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '13px',
              color: 'text.primary',
              pl: `${paddingLeftForDepth(depth)}px`,
              pt: depth > 0 ? 0.5 : 0,
              pb: 0.25,
            }}
          >
            {item.label}
          </Typography>
          {item.children!.map(child => renderLesson(child, depth + 1))}
        </Box>
      );
    }

    if (hasChildren && hasVideoOrPlaceholder) {
      return (
        <Box key={item.id}>
          {renderClickableRow(item, depth)}
          {item.children!.map(child => renderLesson(child, depth + 1))}
        </Box>
      );
    }

    return renderClickableRow(item, depth);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          maxWidth: showNavPanel ? '1100px' : '800px',
          width: '90vw',
          margin: 2,
        },
        zIndex: 10000,
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 1,
          top: 1,
          color: 'grey.500',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: '16px 24px 24px', overflow: 'hidden' }}>
        <Typography
          component="p"
          sx={{
            fontWeight: 400,
            color: 'text.secondary',
            opacity: 0.72,
            mb: 0.75,
            pr: 4,
            fontSize: '11px',
            lineHeight: 1.4,
          }}
        >
          {TUTORIAL_COURSE_HEADING}
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: 'text.primary',
            pr: 4,
          }}
        >
          {displayTitle}
        </Typography>

        {showHeaderDescription && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}
          >
            {headerDescription}
          </Typography>
        )}

        {!noProgressBar && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 2,
              height: 4,
              borderRadius: 1,
            }}
            color="primary"
          />
        )}

        <Box sx={{ display: 'flex', gap: 2.5 }}>
          <Box sx={{ flex: showNavPanel ? '1 1 65%' : '1 1 100%', minWidth: 0 }}>
            <Box
              sx={{
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
                borderRadius: 2,
                bgcolor: '#000',
                '& iframe': {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                },
              }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${content.videoId}?autoplay=1&rel=0`}
                title="Tutorial Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </Box>

          {showNavPanel && (
            <Box
              sx={{
                flex: '0 0 280px',
                borderLeft: '1px solid',
                borderColor: 'divider',
                pl: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                overflowY: 'auto',
                maxHeight: '400px',
              }}
            >
              {tutorialNavModules.map((mod, modIndex) => (
                <React.Fragment key={mod.id}>
                  <Typography
                    variant="subtitle2"
                    component="div"
                    sx={{
                      fontWeight: 700,
                      fontSize: '12px',
                      color: 'text.secondary',
                      px: '12px',
                      mt: modIndex > 0 ? 2 : 0,
                      mb: 0.5,
                      lineHeight: 1.4,
                      textTransform: 'none',
                    }}
                  >
                    {mod.moduleLabel}
                  </Typography>
                  {mod.lessons.map(lesson => renderLesson(lesson, 0))}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Watch Later
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              bgcolor: COLOR_PRIMARY,
              color: '#fff',
              '&:hover': {
                bgcolor: COLOR_PRIMARY,
              },
            }}
          >
            {continueButtonLabel ?? 'Continue to Dashboard'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialModal;
